import { useEffect, useRef, useState } from 'react';
import type { Manager, Socket } from 'socket.io-client';
import { createSocketManager } from '@/lib/socket';
import type { SocketConfig } from '@/types/sale';
import type { CashlessState } from '@/types/payment';

interface LatestStatusPayload {
  status: 'waiting' | 'insufficient' | 'success' | 'canceled' | string;
  data?: { card_balance?: number; card_number?: string };
}

interface UseCashlessSocketArgs {
  config: SocketConfig | null;
  open: boolean;
}

interface UseCashlessSocketReturn {
  state: CashlessState;
  /** Emit a cancel to the reader (waiting/insufficient phases). */
  cancel: () => void;
  /** Reset local state back to idle (e.g. on dialog close). */
  reset: () => void;
}

const IDLE: CashlessState = { phase: 'idle' };

/**
 * Subscribe to the cashless reader's `latest_status` channel and reduce it to
 * a state machine. Ported from reference `cashless-payment.js`.
 */
export function useCashlessSocket({
  config,
  open,
}: UseCashlessSocketArgs): UseCashlessSocketReturn {
  const [state, setState] = useState<CashlessState>(IDLE);
  const managerRef = useRef<Manager | null>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!open || !config?.url || !config?.token || !config?.namespace) {
      return;
    }

    setState({ phase: 'connecting' });

    const manager = createSocketManager(config.url, { forceNew: false });
    managerRef.current = manager;
    const socket = manager.socket(config.namespace, { auth: { token: config.token } });
    socketRef.current = socket;

    socket.on('connect', () => {
      setState((prev) => (prev.phase === 'connecting' ? { phase: 'waiting' } : prev));
    });

    socket.on('latest_status', (raw: string) => {
      try {
        const data: LatestStatusPayload = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (data.status === 'canceled') {
          setState({ phase: 'canceled' });
          socket.emit('force_disconnect', 'close');
        } else if (data.status === 'insufficient') {
          setState({
            phase: 'insufficient',
            cardBalance: data.data?.card_balance,
            cardNumber: data.data?.card_number,
          });
        } else if (data.status === 'success') {
          setState({
            phase: 'success',
            cardBalance: data.data?.card_balance,
            cardNumber: data.data?.card_number,
          });
          socket.emit('force_disconnect', 'close');
        }
      } catch {
        /* malformed payload — ignore */
      }
    });

    socket.on('disconnect', (reason: string) => {
      if (reason === 'io client disconnect') {
        setState((prev) =>
          prev.phase === 'success' ? prev : { phase: 'canceled' },
        );
      }
      socket.off();
    });

    socket.on('connect_error', () => {
      setState({ phase: 'error', message: 'Tidak dapat terhubung ke pembaca kartu.' });
    });
    socket.on('connection_failed', () => {
      setState({ phase: 'error', message: 'Koneksi pembaca kartu gagal.' });
    });

    return () => {
      try {
        socket.removeAllListeners();
        socket.disconnect();
      } catch {
        /* ignore */
      }
      socketRef.current = null;
      managerRef.current = null;
    };
  }, [open, config?.url, config?.token, config?.namespace]);

  function cancel() {
    const s = socketRef.current;
    if (s && (state.phase === 'waiting' || state.phase === 'insufficient')) {
      s.emit('cancel_transaction', 'cancel');
    }
  }

  function reset() {
    setState(IDLE);
  }

  return { state, cancel, reset };
}
